import useRefreshData from "src/hooks/useRefreshData";
import {
  getATECases,
  resetFilterData,
  selectATECasesForExport,
  setExportData,
  setFilterDataField,
  setGridCurrentPage,
} from "src/redux/slices/ateCasesSlice";
import ATECasesGrid from "./ATECasesGrid";
import "./ATECases.scss";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { ReactComponent as ExportIcon } from "../../../assets/icons/export.svg";
import { useTranslation } from "react-i18next";
import ATECasesFilter from "./ATECasesFilter";
import { useCallback, useEffect, useState } from "react";
import { formatDateV2, isCivilDefenseUser, isRtl } from "src/utils/helpers";
import {
  ATECasesOrderedColumns,
  CDATECasesOrderedColumns,
  CDBreachedATECasesOrderedColumns,
} from "src/resources/constant";
import { DownloadViewPrefixes } from "src/resources/enum";
import HorizontalDivider from "src/components/HorizontalDivider/HorizontalDivider";
import { exportFormattedData } from "src/utils/common";
import _ from "lodash";
import ShowHistoricalData from "src/components/ShowHistoricalData/ShowHistoricalData";
import { setChangeHistoricalDataSource } from "src/redux/slices/sharedSlice";

const ATECases = ({ isBreached = false, isAllCases = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "registration", "assignedBuildings"]);
  const { showHistoricalDataChangeSource } = useSelector(
    (state: any) => state.shared
  );
  const { showHistoricalData } = useSelector(
    (state: any) => state.ateCases.filterData
  );
  const [shouldExport, setShouldExport] = useState(false);

  const breadCrumbsPaths = useSelector(
    (state: any) => state.breadCrumb.breadCrumbItems
  );
  const language = useSelector((state: any) => state.lookup.language);

  const ateCasesDataForExport = useSelector(selectATECasesForExport);

  useRefreshData(getATECases);

  useEffect(() => {
    dispatch(resetFilterData());
    dispatch(setExportData([]));
  }, []);

  const exportFile = useCallback(() => {
    if (ateCasesDataForExport.length > 0) {
      const isCivilUser = isCivilDefenseUser();
      let mappedData = getMappedData(ateCasesDataForExport, language);
      let columns = isCivilUser
        ? _.cloneDeep(CDATECasesOrderedColumns)
        : _.cloneDeep(ATECasesOrderedColumns);

      let fileNamePrefix = DownloadViewPrefixes.ATECases;
      if (isCivilUser) {
        if (isBreached) {
          fileNamePrefix = DownloadViewPrefixes.BreachedCases;
          columns = CDBreachedATECasesOrderedColumns;
        } else if (isAllCases) {
          fileNamePrefix = DownloadViewPrefixes.AllCases;
          columns.splice(columns.length - 1, 0, "resolutionDate");
        }
      }
      exportFormattedData(mappedData, columns, fileNamePrefix, t);
    }
  }, [ateCasesDataForExport, language, t]);

  useEffect(() => {
    if (shouldExport) {
      exportFile();
      setShouldExport(false);
    }
  }, [exportFile, shouldExport]);

  const exportExcelHandler = (): void => {
    dispatch(getATECases(true) as any).then(() => {
      setShouldExport(true);
    });
  };
  const handleFormFieldChange = (e: any) => {
    const name = e.target.name;
    const value = e.target.checked;
    dispatch(setFilterDataField({ field: name, value }));
  };
  useEffect(() => {
    dispatch(setGridCurrentPage(0));
  }, [dispatch]);

  useEffect(() => {
    if(showHistoricalDataChangeSource === "") return;
    const index = parseInt(showHistoricalDataChangeSource.toString());
    if (index >= 10) //the number that we set in the ShowHistoricalData component
      dispatch(setGridCurrentPage(0));
      dispatch(setChangeHistoricalDataSource(""));
  }, [showHistoricalDataChangeSource]);
  
  return (
    <div className="py-2">
      <div className="d-flex justify-content-between px-3 align-items-center pb-2">
        {breadCrumbsPaths.length && (
          <h2 className="inner-page-title">
            {t(breadCrumbsPaths[breadCrumbsPaths.length - 1].label)}
          </h2>
        )}
        <div className="gap-2 inner-page-search">
          {!isBreached && (
            <ShowHistoricalData
              handleFormFieldChange={handleFormFieldChange}
              showHistoricalData={showHistoricalData}
            />
          )}

          <Button
            className="d-flex gap-2 light-primary-btn m-0"
            label={t("export")}
            icon={<ExportIcon className="m-0" />}
            onClick={exportExcelHandler}
          ></Button>
        </div>
      </div>
      <HorizontalDivider />
      <ATECasesFilter />
      <HorizontalDivider />
      <ATECasesGrid />
    </div>
  );
};

export default ATECases;

function getMappedData(ateCasesDataForExport: any, language: any) {
  let mappedData = ateCasesDataForExport;
  mappedData = mappedData.map((ateCase: any) => ({
    ...ateCase,
    errorCode: ateCase?.deviceErrorCode,
    errorCodeDescription: ateCase.deviceErrorCodeDescription,
    createdOn: formatDateV2(ateCase.createdOn),
    systemName: ateCase.systemService,
    propertyID: ateCase.buildingNumber,
    propertyName: ateCase.buildingName,
    contractReferenceNumber: ateCase.contractNumber,
    technicianName: ateCase.engineerTechnicianName,
    creationDate: ateCase.createdOn,
    status:
      isRtl(language) && ateCase?.statusAr
        ? ateCase?.statusAr
        : ateCase?.status,
      typeOfApproval:
      isRtl(language) && ateCase?.onHoldReasonAr
        ? ateCase?.onHoldReasonAr
        : ateCase?.onHoldReasonEn,
    sLABreached: isRtl(language)
      ? ateCase?.sLABreachedAr
      : ateCase?.sLABreached,
  }));

  return mappedData;
}
